import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, isSupabaseConfigured } from "./supabase";

/* Valores atuais do site — usados como fallback quando o Supabase
   não está configurado ou está indisponível. O site nunca quebra. */

export const DEFAULT_SETTINGS = {
    whatsapp_number: "5517991995543",
    whatsapp_message:
        "Olá! Gostaria de saber mais sobre os passeios de jet ski em Ubatuba.",
    phone_display: "+55 (17) 99199-5543",
    instagram_handle: "@jetskiparadiseubatuba",
    instagram_url: "https://instagram.com/jetskiparadiseubatuba",
    hero_price: "R$150",
    availability_status: "disponivel",
    availability_message: "",
};

export const DEFAULT_TOURS = [
    {
        id: "jet",
        tag: "O clássico",
        title: "Passeio de Jet Ski",
        description:
            "Acelere pelas águas de Ubatuba e descubra praias e ilhas paradisíacas com muita adrenalina.",
        duration: "",
        price: null,
        promo_price: null,
        image_url: `${process.env.PUBLIC_URL}/jetski-gtx.jpg`,
        image_alt:
            "Jet ski Sea-Doo GTX roxo em águas cristalinas de Ubatuba",
        wa_message: "Olá! Quero reservar um passeio de jet ski em Ubatuba.",
        sort_order: 0,
        active: true,
    },
];

export const DEFAULT_GALLERY = [
    {
        id: "g1",
        url: `${process.env.PUBLIC_URL}/galeria-1.jpg`,
        alt: "Jet ski em curva levantando um jato de água no mar de Ubatuba",
        is_primary: true,
        sort_order: 0,
    },
    {
        id: "g2",
        url: `${process.env.PUBLIC_URL}/galeria-2.jpg`,
        alt: "Visão do piloto no comando do Sea-Doo, com praia e morros ao fundo",
        is_primary: false,
        sort_order: 1,
    },
    {
        id: "g3",
        url: `${process.env.PUBLIC_URL}/galeria-3.jpg`,
        alt: "Sea-Doo GTX roxo em águas cristalinas de Ubatuba",
        is_primary: false,
        sort_order: 2,
    },
];

export const DEFAULT_CONTENT = {
    hero: {
        title: "O PARAÍSO\nÉ MELHOR\nACELERADO",
        subtitle:
            "Conheça Ubatuba a bordo do nosso Sea-Doo GTI SE 170. Escolha a duração do passeio e aproveite uma experiência especial no mar.",
        body: "",
        extra: { eyebrow: "Jetski Paradise • Ubatuba — SP" },
    },
    passeios: {
        title: "Escolha sua aventura",
        subtitle: "",
        body: "",
        extra: { label: "Nossos passeios" },
    },
    contato: {
        title: "Pronto para acelerar?",
        subtitle:
            "Fale direto com a equipe do Jetski Paradise e garanta seu passeio pelas águas de Ubatuba.",
        body: "",
        extra: {
            label: "Chame no nosso link",
            note: "11,6 mil seguidores já acompanham a aventura",
        },
    },
    footer: {
        title: "",
        subtitle: "",
        body: "Passeios de jet ski pelos lugares paradisíacos de Ubatuba — litoral norte de São Paulo.",
        extra: {},
    },
    cancelamento: {
        title: "Política de cancelamento e reembolso",
        subtitle: "Entenda as propostas de reagendamento, cancelamento, segurança e devolução de valores para os passeios de jet ski do Jet Paradise, em Ubatuba.",
        body: "## 📅 Cancelamentos solicitados pelo cliente\n### Mais de 48 horas antes do passeio\nProposta: devolução de 100% do que foi pago, quando o pedido for recebido com mais de 48 horas de antecedência, respeitados os direitos previstos em lei.\n### Entre 24 e 48 horas antes\nProposta: oferecer reagendamento sem custo para uma data disponível ou avaliar cancelamento com retenção de até 30% do valor contratado, quando isso for legalmente admissível e tiver sido informado antes da reserva.\n### Com menos de 24 horas\nProposta: o reagendamento poderá ser analisado conforme a agenda. Se houver desistência, eventual retenção de até 50% dependerá de previsão contratual válida, proporcionalidade e respeito à legislação.\n## 👤 Ausência no horário reservado\nO não comparecimento sem aviso será analisado pela equipe. Qualquer valor retido deverá respeitar os direitos do consumidor e as condições efetivamente aceitas na contratação.\n## 🌊 Condições do mar e segurança\nVento intenso, ondas, raios, baixa visibilidade ou problemas operacionais podem exigir adiamento, alteração ou cancelamento do passeio. Se o Jet Paradise não puder realizar a atividade por segurança, serão oferecidas soluções compatíveis com o caso e com a lei, como uma nova data sem cobrança extra ou reembolso do serviço não prestado.\nA ocorrência de chuva, isoladamente, não determina o cancelamento: a decisão depende da avaliação das condições gerais de navegação.\n## ⏰ Horário de chegada e atrasos\nCompareça no horário confirmado pela equipe. Atrasos podem impactar a duração ou a possibilidade de saída. Pedidos de reagendamento ou devolução serão avaliados conforme as circunstâncias e os direitos aplicáveis.\n## 🛟 Interrupções durante o passeio\nSe a pessoa decidir encerrar o passeio após seu início, a equipe verificará a situação e as condições contratadas. Caso haja interrupção por falha operacional ou motivo de segurança, o atendimento buscará uma solução proporcional ao serviço não realizado, quando cabível.\n## 💳 Devolução de pagamentos\nApós análise e aprovação do pedido, o estorno será solicitado, sempre que possível, pelo meio de pagamento original. A confirmação e a disponibilização do crédito também dependem dos prazos do banco, da operadora do cartão ou da plataforma financeira, sem prejuízo das obrigações legais do fornecedor.\n## ⚖️ Direito de arrependimento\nNas contratações realizadas fora do estabelecimento comercial, o consumidor dispõe, quando aplicável, do prazo de sete dias previsto no artigo 49 do Código de Defesa do Consumidor, com devolução integral dos valores pagos. As condições comerciais propostas nesta página não restringem esse direito.\n## 💬 Pedidos de cancelamento e reagendamento\nEntre em contato com o Jet Paradise pelo canal de atendimento oficial, informando nome, data do passeio e código da reserva, quando houver. A equipe orientará sobre os próximos passos.",
        extra: { status: "minuta", needs_approval: true },
    },
    privacidade: {
        title: "Política de privacidade",
        subtitle: "Saiba como as informações pessoais podem ser usadas no atendimento e nas reservas de passeios de jet ski do Jet Paradise, em Ubatuba, conforme a LGPD.",
        body: "## Quem é responsável pelos dados\nPara esta política, “Jet Paradise” identifica a operação de passeios de jet ski divulgada neste site. A razão social, o CNPJ, o endereço de contato e o canal específico para solicitações sobre dados pessoais ainda precisam ser confirmados pela empresa antes da versão definitiva.\nAté essa confirmação, dúvidas sobre privacidade podem ser encaminhadas pelos canais oficiais de contato exibidos no próprio site, identificando o assunto como “Privacidade e proteção de dados”.\n## Quais informações podem ser coletadas\nEm solicitações de reserva e atendimento, poderão ser informados nome, e-mail, telefone/WhatsApp, data de nascimento, data e horário pretendidos, quantidade de participantes e observações enviadas pelo cliente.\nConforme o funcionamento dos serviços, dados técnicos necessários à operação do site, como endereço IP, informações do navegador e registros de acesso, também poderão ser processados pelos provedores de infraestrutura.\nNa prévia de agendamento atualmente disponível, horários e pagamentos são demonstrativos. Não informe dados financeiros reais nesse ambiente.\n## Para que os dados são utilizados\nOs dados podem ser utilizados para responder dúvidas, registrar solicitações, organizar a disponibilidade, confirmar passeios, informar alterações de horário ou condições do mar e prestar suporte antes e depois da experiência.\nQuando a cobrança online estiver habilitada e devidamente configurada, os dados necessários ao pagamento poderão ser tratados para processar a transação, identificar pagamentos, prevenir fraudes e cumprir obrigações legais.\nNão se deve utilizar informações fornecidas para a reserva em campanhas promocionais sem base legal apropriada e a devida transparência ao cliente.\n## Bases legais do tratamento\nConforme cada situação, o tratamento poderá se fundamentar na execução de contrato ou de procedimentos preliminares solicitados pelo cliente, no cumprimento de obrigação legal ou regulatória e, quando cabível, em legítimo interesse devidamente avaliado.\nO consentimento poderá ser solicitado para finalidades opcionais que dependam dessa base legal, de forma específica e com possibilidade de revogação, observadas as regras da LGPD.\n## Compartilhamento com serviços necessários\nPara operar o site e administrar solicitações, poderão ser utilizados prestadores de hospedagem e infraestrutura, como Render, e serviços de armazenamento e autenticação, como Supabase. As atividades e os acessos efetivamente configurados deverão ser conferidos pela empresa.\nSe o cliente escolher contatar a equipe pelo WhatsApp, essa comunicação também se sujeitará às práticas de privacidade da plataforma.\nO Mercado Pago é o gateway planejado para pagamentos futuros; a cobrança online real ainda depende de ativação e validação. Ao ser habilitada, a empresa deverá informar quais dados são enviados ao provedor.\nAlguns prestadores podem realizar tratamento ou armazenamento fora do Brasil. A empresa deverá verificar as condições de transferência internacional e as salvaguardas exigidas pela LGPD, quando aplicáveis.\n## Como os dados são protegidos\nO projeto deve adotar controles de acesso, autenticação e medidas técnicas e administrativas proporcionais aos riscos para reduzir acessos indevidos, perda, alteração ou divulgação não autorizada de dados pessoais.\nNenhum sistema é totalmente imune a incidentes. Eventuais ocorrências relevantes serão avaliadas e tratadas conforme as obrigações legais aplicáveis.\nEsta política não substitui uma auditoria de segurança nem comprova, por si só, que todas as medidas descritas já foram verificadas em produção.\n## Por quanto tempo os dados são mantidos\nOs dados deverão ser conservados somente pelo período necessário às finalidades informadas, ao cumprimento de obrigações legais, à prestação de contas e ao exercício regular de direitos, observadas as hipóteses de eliminação previstas na LGPD.\nOs prazos específicos para registros de reservas, comprovantes de pagamento, logs técnicos e solicitações dos clientes ainda precisam ser definidos e documentados pela empresa.\n## Direitos de quem fornece os dados\nNos termos da LGPD e conforme os requisitos aplicáveis, o titular pode solicitar confirmação de tratamento, acesso, correção, anonimização, bloqueio ou eliminação de dados inadequados, portabilidade e informações sobre compartilhamentos.\nTambém pode pedir informações sobre a possibilidade de não consentir, revogar consentimentos quando essa for a base legal utilizada e solicitar revisão de decisões automatizadas, nas hipóteses previstas na legislação.\nOs pedidos serão analisados pelos canais oficiais e atendidos conforme a legislação, inclusive nas situações em que seja necessário conservar dados por obrigação legal.\n## Dados de crianças e adolescentes\nQuando a participação de menores for admitida pelas regras operacionais, o tratamento de seus dados deverá respeitar o melhor interesse da criança ou do adolescente e os requisitos aplicáveis da LGPD.\nA confirmação de responsáveis e os dados estritamente necessários à segurança do passeio deverão ser definidos antes da liberação das reservas reais.\n## Cookies, atualizações e contato\nO site e seus serviços de infraestrutura podem utilizar recursos técnicos essenciais ao funcionamento, como armazenamento de sessão e registros de acesso. Antes da publicação definitiva, a empresa deverá identificar eventuais ferramentas de análise e cookies não essenciais e definir como serão informados aos visitantes.\nEsta política poderá ser atualizada quando houver mudanças nas funcionalidades, nos prestadores, nos dados coletados ou nas exigências legais.\nPara exercer seus direitos ou solicitar esclarecimentos, utilize os contatos oficiais divulgados no Jet Paradise e escreva “Privacidade e proteção de dados”. A identificação completa da empresa e o contato específico de privacidade estão pendentes de validação.",
        extra: { status: "minuta", needs_approval: true },
    },
};

function useTable(queryKey, table, build) {
    return useQuery({
        queryKey,
        enabled: isSupabaseConfigured,
        retry: false,
        staleTime: 60_000,
        queryFn: async () => {
            const { data, error } = await build(supabase.from(table));
            if (error) throw error;
            return data;
        },
    });
}

export function useSiteSettings() {
    const q = useTable(
        ["site-settings"],
        "site_settings",
        (t) => t.select("*").eq("id", 1).maybeSingle(),
    );
    return { ...DEFAULT_SETTINGS, ...(q.data || {}) };
}

export function useTours(all = false) {
    const q = useTable(["tours", all], "tours", (t) => {
        let s = t.select("*");
        if (!all) s = s.eq("active", true);
        return s.order("sort_order").order("created_at");
    });
    return q.data ?? DEFAULT_TOURS;
}

export function useLocations(all = false) {
    const q = useTable(["locations", all], "locations", (t) => {
        let s = t.select("*");
        if (!all) s = s.eq("active", true);
        return s.order("sort_order").order("created_at");
    });
    return q.data ?? [];
}

export function useGallery() {
    const q = useTable(["gallery"], "gallery_images", (t) =>
        t.select("*").order("sort_order").order("created_at"),
    );
    return q.data ?? DEFAULT_GALLERY;
}

export function useContentRows() {
    const q = useTable(["content"], "content_sections", (t) => t.select("*"));
    return q.data ?? [];
}

export function useContentSection(key) {
    const rows = useContentRows();
    const base =
        DEFAULT_CONTENT[key] || { title: "", subtitle: "", body: "", extra: {} };
    const row = rows.find((r) => r.key === key);
    if (!row) return base;
    return {
        ...base,
        ...row,
        extra: { ...base.extra, ...(row.extra || {}) },
    };
}

/* Helpers derivados das configurações (fonte única do WhatsApp/Instagram). */

export function waLinkFrom(settings, msg) {
    const num = settings.whatsapp_number || DEFAULT_SETTINGS.whatsapp_number;
    const text = msg || settings.whatsapp_message;
    return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
}

export function instaUrlFrom(settings) {
    return settings.instagram_url || DEFAULT_SETTINGS.instagram_url;
}

export function formatPrice(v) {
    if (v === null || v === undefined || v === "") return null;
    return `R$ ${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })}`;
}

/* Escrita (painel admin) — exige sessão de administrador (RLS). */

export function useSaveSettings() {
    const qc = useQueryClient();
    return async (patch) => {
        const { error } = await supabase
            .from("site_settings")
            .upsert({ id: 1, ...patch, updated_at: new Date().toISOString() });
        if (error) throw error;
        qc.invalidateQueries({ queryKey: ["site-settings"] });
    };
}

export function useInvalidate() {
    const qc = useQueryClient();
    return (key) => qc.invalidateQueries({ queryKey: [key] });
}
